// ChartWidgets.tsx - Recharts-based chart components for dashboard
import React from 'react';
import {
  BarChart,
  Bar,
  LineChart,
  Line,
  PieChart,
  Pie,
  Cell,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  AreaChart,
  Area,
  Legend
} from 'recharts';

const COLORS = ['#36CCD8', '#2DB8C4', '#10B981', '#F59E0B', '#EF4444', '#8B5CF6'];

const EmptyChartState = () => <div className="h-[200px] flex items-center justify-center text-sm text-gray-500">Sin datos para el rango seleccionado</div>;

interface ChartCardProps {
  title: string;
  subtitle?: string;
  children: React.ReactNode;
  className?: string;
}

export const ChartCard: React.FC<ChartCardProps> = ({ title, subtitle, children, className = '' }) => (
  <div className={`bg-white rounded-2xl p-6 shadow-sm border border-gray-100 ${className}`}>
    <div className="mb-4">
      <h3 className="text-lg font-bold text-[#2D3748]">{title}</h3>
      {subtitle && <p className="text-sm text-gray-500">{subtitle}</p>}
    </div>
    {children}
  </div>
);

// Revenue Chart (Area Chart)
interface RevenueChartProps {
  data: { month: string; revenue: number; expenses: number }[];
}

export const RevenueChart: React.FC<RevenueChartProps> = ({ data }) => (
  <ChartCard title="Ingresos vs Egresos" subtitle="Comparación mensual">
    {data.length === 0 ? <EmptyChartState /> : <ResponsiveContainer width="100%" height={250}>
      <AreaChart data={data}>
        <defs>
          <linearGradient id="colorRevenue" x1="0" y1="0" x2="0" y2="1">
            <stop offset="5%" stopColor="#36CCD8" stopOpacity={0.3}/>
            <stop offset="95%" stopColor="#36CCD8" stopOpacity={0}/>
          </linearGradient>
          <linearGradient id="colorExpenses" x1="0" y1="0" x2="0" y2="1">
            <stop offset="5%" stopColor="#EF4444" stopOpacity={0.3}/>
            <stop offset="95%" stopColor="#EF4444" stopOpacity={0}/>
          </linearGradient>
        </defs>
        <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
        <XAxis dataKey="month" tick={{ fill: '#718096', fontSize: 12 }} axisLine={false} tickLine={false} />
        <YAxis tick={{ fill: '#718096', fontSize: 12 }} axisLine={false} tickLine={false} tickFormatter={(value) => `$${Number(value)/1000}k`} />
        <Tooltip 
          contentStyle={{ borderRadius: 12, border: 'none', boxShadow: '0 4px 6px -1px rgba(0, 0, 0, 0.1)' }}
        />
        <Legend wrapperStyle={{ paddingTop: 20 }} />
        <Area type="monotone" dataKey="revenue" stroke="#36CCD8" fillOpacity={1} fill="url(#colorRevenue)" name="Ingresos" strokeWidth={2} />
        <Area type="monotone" dataKey="expenses" stroke="#EF4444" fillOpacity={1} fill="url(#colorExpenses)" name="Egresos" strokeWidth={2} />
      </AreaChart>
    </ResponsiveContainer>}
  </ChartCard>
);

// Visitor Traffic Chart (Line Chart)
interface TrafficChartProps {
  data: { day: string; visitors: number }[];
}

export const TrafficChart: React.FC<TrafficChartProps> = ({ data }) => (
  <ChartCard title="Tráfico de Visitantes" subtitle="Últimos 30 días">
    {data.length === 0 ? <EmptyChartState /> : <ResponsiveContainer width="100%" height={200}>
      <LineChart data={data}>
        <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
        <XAxis dataKey="day" tick={{ fill: '#718096', fontSize: 12 }} axisLine={false} tickLine={false} />
        <YAxis tick={{ fill: '#718096', fontSize: 12 }} axisLine={false} tickLine={false} />
        <Tooltip 
          contentStyle={{ borderRadius: 12, border: 'none', boxShadow: '0 4px 6px -1px rgba(0, 0, 0, 0.1)' }}
        />
        <Line 
          type="monotone" 
          dataKey="visitors" 
          stroke="#36CCD8" 
          strokeWidth={3}
          dot={{ fill: '#36CCD8', strokeWidth: 2, r: 4 }}
          activeDot={{ r: 6, strokeWidth: 0 }}
        />
      </LineChart>
    </ResponsiveContainer>}
  </ChartCard>
);

// Payment Distribution (Pie Chart)
interface PaymentDistributionProps {
  data: { name: string; value: number }[];
}

export const PaymentDistributionChart: React.FC<PaymentDistributionProps> = ({ data }) => (
  <ChartCard title="Distribución de Pagos">
    {data.length === 0 ? <EmptyChartState /> : <ResponsiveContainer width="100%" height={250}>
      <PieChart>
        <Pie
          data={data}
          cx="50%"
          cy="50%"
          innerRadius={60}
          outerRadius={80}
          paddingAngle={5}
          dataKey="value"
        >
          {data.map((entry, index) => (
            <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
          ))}
        </Pie>
        <Tooltip contentStyle={{ borderRadius: 12, border: 'none' }} />
        <Legend verticalAlign="bottom" height={36} />
      </PieChart>
    </ResponsiveContainer>}
  </ChartCard>
);

// Occupancy Bar Chart
interface OccupancyChartProps {
  data: { unit: string; occupied: number; available: number }[];
}

export const OccupancyChart: React.FC<OccupancyChartProps> = ({ data }) => (
  <ChartCard title="Ocupación">
    {data.length === 0 ? <EmptyChartState /> : <ResponsiveContainer width="100%" height={200}>
      <BarChart data={data}>
        <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" vertical={false} />
        <XAxis dataKey="unit" tick={{ fill: '#718096', fontSize: 12 }} axisLine={false} tickLine={false} />
        <YAxis tick={{ fill: '#718096', fontSize: 12 }} axisLine={false} tickLine={false} />
        <Tooltip contentStyle={{ borderRadius: 12, border: 'none' }} />
        <Legend />
        <Bar dataKey="occupied" name="Ocupado" fill="#36CCD8" radius={[4, 4, 0, 0]} />
        <Bar dataKey="available" name="Disponible" fill="#10B981" radius={[4, 4, 0, 0]} />
      </BarChart>
    </ResponsiveContainer>}
  </ChartCard>
);

// Stats Grid
interface StatItem {
  label: string;
  value: string;
  change?: string;
  changeType?: 'positive' | 'negative' | 'neutral';
  icon: React.ElementType;
}

interface StatsGridProps {
  stats: StatItem[];
}

export const StatsGrid: React.FC<StatsGridProps> = ({ stats }) => (
  <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
    {stats.map((stat, index) => {
      const Icon = stat.icon;
      return (
        <div key={index} className="bg-white rounded-2xl p-5 shadow-sm border border-gray-100 hover:shadow-md transition-shadow">
          <div className="flex items-start justify-between">
            <div>
              <p className="text-sm text-gray-500 mb-1">{stat.label}</p>
              <p className="text-2xl font-bold text-[#2D3748]">{stat.value}</p>
              {stat.change && (
                <p className={`text-xs mt-1 ${
                  stat.changeType === 'positive' ? 'text-green-500' : 
                  stat.changeType === 'negative' ? 'text-red-500' : 'text-gray-500'
                }`}>
                  {stat.change}
                </p>
              )}
            </div>
            <div className="w-10 h-10 bg-gradient-to-br from-[#36CCD8]/20 to-[#2DB8C4]/20 rounded-xl flex items-center justify-center">
              <Icon className="w-5 h-5 text-[#36CCD8]" />
            </div>
          </div>
        </div>
      );
    })}
  </div>
);

export default {
  ChartCard,
  RevenueChart,
  TrafficChart,
  PaymentDistributionChart,
  OccupancyChart,
  StatsGrid
};